"use client";

import { useWebSocket, WSMessage } from "@/providers/websocket-provider";
import {
  InAppNotification,
  NotificationStyle,
  NotificationType,
} from "@/lib/client/notifications-api";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Options for useNotificationsWS hook
 */
export interface UseNotificationsWSOptions {
  /** Callback when a new notification is received */
  onNotificationReceived?: (notification: InAppNotification) => void;
}

/**
 * Hook to subscribe to real-time notification updates via WebSocket
 *
 * This hook handles:
 * - Updating the unread count when it changes
 * - Calling onNotificationReceived when new notifications arrive
 * - Invalidating React Query caches for notification lists
 */
export function useNotificationsWS(options: UseNotificationsWSOptions = {}): {
  /** Current unread count from WebSocket (null if not yet received) */
  unreadCount: number | null;
  /** Last notification received */
  lastNotification: InAppNotification | null;
  /** Whether WebSocket is connected */
  isConnected: boolean;
} {
  const { onNotificationReceived } = options;

  const { subscribe, isConnected } = useWebSocket();
  const queryClient = useQueryClient();
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const [lastNotification, setLastNotification] =
    useState<InAppNotification | null>(null);

  // Store callback in ref to avoid re-subscribing on callback change
  const callbackRef = useRef(onNotificationReceived);

  useEffect(() => {
    callbackRef.current = onNotificationReceived;
  }, [onNotificationReceived]);

  // Subscribe to notification updates
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const topic = "notifications";

    const unsubscribe = subscribe(topic, (message: WSMessage) => {
      if (message.type === "notification" && message.data) {
        const data = message.data as {
          id: string;
          alarm_id?: string;
          device_id?: string;
          title?: string;
          display_name?: string;
          message?: string;
          severity?: string;
          alarm_type?: string;
          device_name?: string;
          notification_style?: NotificationStyle;
          notification_type?: NotificationType;
          created_at?: string;
          action: string;
          unread_count?: number;
        };

        // Handle different notification actions
        if (data.action === "created") {
          // New notification received
          const notification: InAppNotification = {
            id: data.id,
            user_id: "", // Not sent over WS, user already knows who they are
            notification_type:
              data.notification_type ?? (data.alarm_id ? "alarm" : "notification"),
            alarm_id: data.alarm_id,
            device_id: data.device_id,
            title: data.title || data.display_name || "Notification",
            message: data.message,
            severity: data.severity as InAppNotification["severity"],
            alarm_type: data.alarm_type,
            device_name: data.device_name,
            notification_style: data.notification_style,
            created_at: data.created_at || new Date().toISOString(),
          };

          setLastNotification(notification);

          // Call callback if provided
          if (callbackRef.current) {
            callbackRef.current(notification);
          }

          // Invalidate notification queries to refresh lists
          queryClient.invalidateQueries({
            queryKey: ["notifications"],
          });

          // Increment unread count optimistically
          setUnreadCount((prev) => (prev ?? 0) + 1);
        } else if (data.action === "read" || data.action === "dismissed") {
          // Notification was marked as read or dismissed (from another tab/device)
          // Update the unread count if provided
          if (typeof data.unread_count === "number") {
            setUnreadCount(data.unread_count);
            queryClient.setQueryData(
              ["notifications", "unread-count"],
              data.unread_count
            );
          }

          // Invalidate notification queries to refresh lists
          // This ensures the notification list shows the updated read/dismissed state
          queryClient.invalidateQueries({
            queryKey: ["notifications"],
          });
        }
      } else if (message.type === "unread_count" && message.data) {
        // Unread count update (e.g., after marking as read)
        const data = message.data as { count: number };
        setUnreadCount(data.count);

        // Also update the query cache
        queryClient.setQueryData(
          ["notifications", "unread-count"],
          data.count
        );
      }
    });

    return unsubscribe;
  }, [isConnected, subscribe, queryClient]);

  return {
    unreadCount,
    lastNotification,
    isConnected,
  };
}
